import {
  type Approval,
  type Checkpoint,
  type Routing,
  type RoutingContext,
  defaultRouting,
} from './checkpoints';
import type { Profile } from './profiles';
import { parseReviewBody } from './reviewBody';
import {
  ROLE_LABELS,
  type RoleException,
  type RolesFile,
  sameLogin,
} from './roles';

export type PullReview = {
  user: { login: string; type?: string } | null;
  state: string;
  commit_id: string;
  body?: string | null;
  submitted_at?: string;
};

export type CheckpointInput = {
  checkpoint: Checkpoint;
  profile: Profile;
  /** Read from the base branch, never the head. */
  roles: RolesFile['roles'];
  exceptions?: readonly RoleException[];
  /** PR author, verified commit authors and committers, and everyone who pushed. */
  authors: readonly string[];
  reviews: readonly PullReview[];
  headSha: string;
  context?: RoutingContext;
  routing?: Routing;
  today?: string;
};

export type RoleState =
  /** `warning` is set when the approval breaks a separation-of-duties rule. */
  | { state: 'approved'; by: string; warning?: string }
  | { state: 'changes-requested'; by: string; comment?: string }
  | { state: 'waiting'; eligible: string[] }
  | { state: 'nobody-eligible'; holders: string[] };

export type RoleOutcome = Approval & RoleState;

export type CheckpointEvaluation = {
  roles: RoleOutcome[];
  satisfied: boolean;
  conditions: string[];
  /** Approvals given on an earlier head, which GitHub dismisses. */
  stale: string[];
};

const isBot = (user: PullReview['user']) =>
  !user || user.type === 'Bot' || user.login.endsWith('[bot]');

const DECIDING_STATES = new Set(['APPROVED', 'CHANGES_REQUESTED']);

const STATE_OF_DECISION: Partial<Record<string, string>> = {
  Approved: 'APPROVED',
  'Approved with conditions': 'APPROVED',
  Concurs: 'APPROVED',
  'Changes requested': 'CHANGES_REQUESTED',
};

/**
 * GitHub doesn't let authors approve or request changes, so the portal
 * records an author's decision as a comment carrying the review body.
 */
export const reviewAsDecision = (review: PullReview): PullReview => {
  if (review.state !== 'COMMENTED') return review;
  const decision = parseReviewBody(review.body)?.decision;
  const state = decision && STATE_OF_DECISION[decision];
  return state ? { ...review, state } : review;
};

/** The latest deciding review per human. */
function latestDecisions(reviews: readonly PullReview[]) {
  const latest = new Map<string, PullReview>();
  for (const review of reviews.map(reviewAsDecision)) {
    if (isBot(review.user) || !DECIDING_STATES.has(review.state)) continue;
    const key = review.user!.login.toLowerCase();
    const previous = latest.get(key);
    if (
      !previous ||
      (review.submitted_at ?? '') >= (previous.submitted_at ?? '')
    ) {
      latest.set(key, review);
    }
  }
  return [...latest.values()];
}

const combinationAllowed = (
  exceptions: readonly RoleException[],
  login: string,
  today: string,
) =>
  exceptions.some(
    e => sameLogin(e.person, login) && (!e.expires || e.expires >= today),
  );

/**
 * Who has decided a checkpoint and who is still needed (gate-review spec
 * "Eligibility"). Portal-side, best effort until the template's gate check
 * decides. An author's approval, or one person counting for several roles,
 * counts with a warning instead of blocking, so one person can try the flow.
 */
export function evaluateCheckpoint(input: CheckpointInput): CheckpointEvaluation {
  const exceptions = input.exceptions ?? [];
  const today = input.today ?? new Date().toISOString().slice(0, 10);
  const routing = input.routing ?? defaultRouting;
  const required = routing(input.checkpoint, input.profile, input.context);
  const isAuthor = (login: string) =>
    input.authors.some(author => sameLogin(author, login));
  const holders = (approval: Approval) => input.roles[approval.role] ?? [];

  const decisions = latestDecisions(input.reviews);
  const current = decisions.filter(r => r.commit_id === input.headSha);
  const stale = decisions
    .filter(r => r.commit_id !== input.headSha && r.state === 'APPROVED')
    .map(r => r.user!.login);

  // Who approved for which role, to warn when one person counts twice.
  const used = new Map<string, Approval['role']>();
  const outcomes: RoleOutcome[] = required.map(approval => {
    const candidates = holders(approval);
    const byRole = (review: PullReview) =>
      candidates.some(login => sameLogin(login, review.user!.login));
    const rejection = current.find(
      r => r.state === 'CHANGES_REQUESTED' && byRole(r),
    );
    if (rejection) {
      return {
        ...approval,
        state: 'changes-requested',
        by: rejection.user!.login,
        comment: parseReviewBody(rejection.body)?.comment ?? rejection.body ?? undefined,
      };
    }
    const approvals = current.filter(r => r.state === 'APPROVED' && byRole(r));
    // Prefer an approval whose body names this role, then any unused one.
    const named = approvals.find(
      r => parseReviewBody(r.body)?.role === approval.role,
    );
    const ordered = [named, ...approvals].filter(
      (r): r is PullReview => Boolean(r),
    );
    const pick =
      ordered.find(r => !used.has(r.user!.login.toLowerCase())) ?? ordered[0];
    if (pick) {
      const by = pick.user!.login;
      const earlier = used.get(by.toLowerCase());
      used.set(by.toLowerCase(), earlier ?? approval.role);
      const warning = [
        isAuthor(by) ? `${by} also wrote this change.` : '',
        earlier && !combinationAllowed(exceptions, by, today)
          ? `${by} also approved as ${ROLE_LABELS[earlier]}.`
          : '',
      ]
        .filter(Boolean)
        .join(' ');
      return { ...approval, state: 'approved', by, ...(warning && { warning }) };
    }
    return candidates.length > 0
      ? { ...approval, state: 'waiting', eligible: candidates }
      : { ...approval, state: 'nobody-eligible', holders: candidates };
  });

  return {
    roles: outcomes,
    satisfied: outcomes.every(o => o.state === 'approved'),
    conditions: current
      .map(r => parseReviewBody(r.body)?.conditions)
      .filter((c): c is string => Boolean(c)),
    stale,
  };
}

/** Why the signed-in person can't approve, or empty when they can. */
export function approvalBlocker(
  evaluation: CheckpointEvaluation,
  login: string,
): string {
  const open = evaluation.roles.filter(
    o => o.state === 'waiting' && o.eligible.some(e => sameLogin(e, login)),
  );
  return open.length > 0 ? '' : "You don't hold a role this checkpoint still needs.";
}

/** What breaks separation of duties if the signed-in person approves, or empty. */
export function approvalWarning(
  login: string,
  authors: readonly string[],
): string {
  return authors.some(author => sameLogin(author, login))
    ? "You edited this. ASDP expects someone else to approve it; your decision is recorded as a comment, since GitHub doesn't let authors approve."
    : '';
}
